import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
// UPPDRAG-8 PR-E: dual-write result-reported system message.
import { sendLeagueMatchMessage } from "@/lib/chatDualWrite";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import type { Json } from "@/integrations/supabase/types";

interface SetScore {
  home: number | "";
  away: number | "";
  tiebreak: boolean;
  tiebreak_home: number | "";
  tiebreak_away: number | "";
}

// TASK-67 PR-D: dropped player1/player2 slot fields. Roster comes from
// the team_members embed below; legacy slot props (player1/player2) are
// kept on the type as nullable so any straggler reads still type-check
// during the PR-D → PR-E transition. Production callers populate this
// shape from the team_members embed (see the matchData query upstream).
interface TeamMemberEmbed {
  user_id: string | null;
  role: "primary" | "reserve";
  joined_at: string;
  profile?: { full_name: string | null } | null;
}

interface TeamWithPlayers {
  id: string;
  team_name: string | null;
  members?: TeamMemberEmbed[] | null;
}

interface MatchData {
  id: string;
  home_team_id: string;
  away_team_id: string;
  home_team: TeamWithPlayers | null;
  away_team: TeamWithPlayers | null;
  status: string;
  [key: string]: unknown;
}

interface MatchFormat {
  sets?: number;
  tiebreak_at?: string;
  time_limit_applies?: boolean;
}

interface ScoringSystem {
  set_win?: number;
  set_loss?: number;
  unfinished_set_tied?: number;
  // UPPDRAG-39: legacy 5-bucket fields kept for backward read of older
  // results; new computation reads from the dedicated columns on leagues
  // (incomplete_set_lead_threshold / share_winner / share_loser) below.
  unfinished_set_leader_1?: number;
  unfinished_set_trailer_1?: number;
  ["unfinished_set_leader_2plus"]?: number;
  ["unfinished_set_trailer_2plus"]?: number;
}

interface LeagueInfo {
  id: string;
  name: string;
  organization_id: string;
  match_format: Json;
  scoring_system: Json;
  // UPPDRAG-39: configurable incomplete-set leader-rule columns.
  incomplete_set_lead_threshold?: number;
  incomplete_set_share_winner?: number;
  incomplete_set_share_loser?: number;
  [key: string]: unknown;
}

interface ReportResultProps {
  match: MatchData;
  teamId: string;
  league: LeagueInfo;
  onClose: () => void;
  onSuccess: () => void;
}

// UPPDRAG-39: replaced the inline 5-bucket logic with a pure helper backed
// by the new configurable threshold columns. The pure helper lives in
// src/lib/leagueScoringCore.ts and is unit-tested via vitest. Defaults
// (threshold=2, share_winner=1, share_loser=1) reproduce the real padel
// rule: lead by 2 at time-out = SET WIN (uses set_win/set_loss), lead by
// 1 = share, tied = unfinished_set_tied.
import { calculateMatchPoints } from "@/lib/leagueScoringCore";

function calculatePoints(
  sets: SetScore[],
  scoringSystem: ScoringSystem,
  isTimeLimited: boolean,
  incompleteSetRule: { leadThreshold: number; shareWinner: number; shareLoser: number },
): { home: number; away: number } {
  return calculateMatchPoints({
    sets,
    scoringSystem,
    incompleteSetRule,
    isTimeLimited,
  });
}

export default function ReportResult({ match, teamId: _teamId, league, onClose, onSuccess }: ReportResultProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const matchFormat = league.match_format as MatchFormat;
  const scoringSystem = league.scoring_system as ScoringSystem;
  const numSets = matchFormat?.sets || 3;

  const [sets, setSets] = useState<SetScore[]>(
    Array.from({ length: numSets }, () => ({
      home: "", away: "", tiebreak: false, tiebreak_home: "", tiebreak_away: "",
    }))
  );
  const [isWalkover, setIsWalkover] = useState(false);
  const [walkoverTeam, setWalkoverTeam] = useState<"home" | "away">("away");
  const [timeLimited, setTimeLimited] = useState(false);
  const [comment, setComment] = useState("");

  const updateSet = (index: number, field: keyof SetScore, value: SetScore[keyof SetScore]) => {
    setSets((prev) => {
      const next = [...prev];
      const s = { ...next[index], [field]: value };
      // Auto-detect tiebreak
      if (field === "home" || field === "away") {
        const tiebreakAt = matchFormat?.tiebreak_at === "6-6" ? 6 : 6;
        s.tiebreak = Number(s.home) === tiebreakAt && Number(s.away) === tiebreakAt;
      }
      next[index] = s;
      return next;
    });
  };

  const handleWalkover = (checked: boolean) => {
    setIsWalkover(checked);
    if (checked) {
      setSets(
        Array.from({ length: numSets }, () => ({
          home: walkoverTeam === "away" ? 6 : 0,
          away: walkoverTeam === "away" ? 0 : 6,
          tiebreak: false, tiebreak_home: "", tiebreak_away: "",
        }))
      );
    }
  };

  const handleWalkoverTeamChange = (team: "home" | "away") => {
    setWalkoverTeam(team);
    if (isWalkover) {
      setSets(
        Array.from({ length: numSets }, () => ({
          home: team === "away" ? 6 : 0,
          away: team === "away" ? 0 : 6,
          tiebreak: false, tiebreak_home: "", tiebreak_away: "",
        }))
      );
    }
  };

  const validSets = sets.filter((s) => s.home !== "" && s.away !== "");
  // UPPDRAG-39: pull the configurable threshold + share values from the league.
  // Defaults (2/1/1) match the real padel rule and any pre-migration leagues.
  const incompleteSetRule = {
    leadThreshold: league.incomplete_set_lead_threshold ?? 2,
    shareWinner: league.incomplete_set_share_winner ?? 1,
    shareLoser: league.incomplete_set_share_loser ?? 1,
  };
  const points = calculatePoints(sets, scoringSystem, timeLimited, incompleteSetRule);

  const isValid = validSets.length >= 1 && validSets.every((s) => {
    const h = Number(s.home);
    const a = Number(s.away);
    if (h < 0 || h > 7 || a < 0 || a > 7) return false;
    if (h === 7 && a < 5) return false;
    if (a === 7 && h < 5) return false;
    if ((h === 7 || a === 7) && !(h === 7 && a === 6) && !(h === 6 && a === 7) && !(h === 7 && a === 5) && !(a === 7 && h === 5)) return false;
    return true;
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const resultData = {
        sets: sets.map((s) => ({
          home: Number(s.home),
          away: Number(s.away),
          ...(s.tiebreak ? { tiebreak: true, tiebreak_home: Number(s.tiebreak_home), tiebreak_away: Number(s.tiebreak_away) } : {}),
        })).filter((s) => !isNaN(s.home) && !isNaN(s.away)),
        points_home: points.home,
        points_away: points.away,
        walkover: isWalkover,
        time_limited: timeLimited,
        comment,
      };

      const status = isWalkover
        ? walkoverTeam === "away"
          ? "walkover_home"
          : "walkover_away"
        : "completed";

      const { error } = await supabase
        .from("league_matches")
        .update({
          result: resultData as unknown as Json,
          status,
          reported_by: user!.id,
          reported_at: new Date().toISOString(),
        })
        .eq("id", match.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      // Insert system chat message
      const validSetsForMsg = sets.filter((s) => s.home !== "" && s.away !== "");
      const setsSummary = validSetsForMsg.map((s) => `${s.home}-${s.away}`).join(", ");

      // Also save to match_results for unified stats
      try {
        const homeTeam = match.home_team;
        const awayTeam = match.away_team;
        // TASK-67 PR-D: collapsed the previous two-source union (teams.player1/2
        // + team_members.user_id) to team_members alone. PR-A's backfill
        // ensured every prior player1/2 user_id has a team_members row, so
        // the registered roster set is identical. winner_team[] still gets
        // the full registered roster so the `auth.uid() = ANY(winner_team)`
        // RLS predicate on match_results works for everyone on the team.
        const fetchTeamRoster = async (
          team: { id: string } | null,
        ): Promise<string[]> => {
          if (!team?.id) return [];
          const { data: members } = await supabase
            .from("team_members")
            .select("user_id")
            .eq("team_id", team.id)
            .not("user_id", "is", null);
          const set = new Set<string>();
          for (const m of (members ?? []) as { user_id: string | null }[]) {
            if (m.user_id) set.add(m.user_id);
          }
          return Array.from(set);
        };
        const [homePlayers, awayPlayers] = await Promise.all([
          fetchTeamRoster(homeTeam as any),
          fetchTeamRoster(awayTeam as any),
        ]);

        const homeWon = points.home > points.away;
        const winnerTeam = homeWon ? homePlayers : awayPlayers;
        const loserTeam = homeWon ? awayPlayers : homePlayers;

        if (winnerTeam.length > 0 && loserTeam.length > 0 && league.organization_id) {
          await supabase.from("match_results").insert({
            organization_id: league.organization_id,
            match_type: "league",
            league_match_id: match.id,
            winner_team: winnerTeam,
            loser_team: loserTeam,
            score: setsSummary,
            reported_by: user!.id,
            confirmed: false,
          });
        }
      } catch {
        // Non-critical
      }

      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user!.id)
          .single();
        const name = profile?.full_name || "Okänd";
        // UPPDRAG-8 PR-E: dual-write + structured metadata.
        await sendLeagueMatchMessage(supabase, {
          matchId: match.id,
          senderUserId: user!.id,
          body: `Resultat rapporterat: ${setsSummary} av ${name}`,
          isSystem: true,
          data: {
            type: "result_reported",
            sets_summary: setsSummary,
            reporter_name: name,
            reporter_user_id: user!.id,
          },
        });
      } catch {
        // Non-critical, don't block
      }
      toast({ title: "Resultat rapporterat!" });
      queryClient.invalidateQueries({ queryKey: ["league-matches"] });
      queryClient.invalidateQueries({ queryKey: ["match-detail", match.id] });
      queryClient.invalidateQueries({ queryKey: ["division-standings"] });
      queryClient.invalidateQueries({ queryKey: ["match-chat", match.id] });
      queryClient.invalidateQueries({ queryKey: ["match-results"] });
      queryClient.invalidateQueries({ queryKey: ["player-stats"] });
      onSuccess();
    },
    onError: (err: Error) => {
      toast({ title: "Fel", description: err.message, variant: "destructive" });
    },
  });

  const homeLabel = match.home_team?.team_name || "Hemma";
  const awayLabel = match.away_team?.team_name || "Borta";

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Rapportera resultat</h3>

      {/* Walkover */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="walkover"
          checked={isWalkover}
          onCheckedChange={(v) => handleWalkover(!!v)}
        />
        <Label htmlFor="walkover">Walkover</Label>
      </div>
      {isWalkover && (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={walkoverTeam === "away" ? "default" : "outline"}
            onClick={() => handleWalkoverTeamChange("away")}
          >
            {awayLabel} WO
          </Button>
          <Button
            size="sm"
            variant={walkoverTeam === "home" ? "default" : "outline"}
            onClick={() => handleWalkoverTeamChange("home")}
          >
            {homeLabel} WO
          </Button>
        </div>
      )}

      {/* Time limited */}
      {matchFormat?.time_limit_applies && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="timeLimited"
            checked={timeLimited}
            onCheckedChange={(v) => setTimeLimited(!!v)}
          />
          <Label htmlFor="timeLimited">Matchen avbröts pga tid</Label>
        </div>
      )}

      {/* Sets */}
      <div className="space-y-3">
        {sets.map((set, i) => (
          <div key={i} className="space-y-1">
            <Label className="text-xs">Set {i + 1}</Label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input type="number"
                  min={0}
                  max={7}
                  placeholder={homeLabel}
                  value={set.home}
                  onChange={(e) => updateSet(i, "home", e.target.value === "" ? "" : Number(e.target.value))}
                  disabled={isWalkover}
                  className="text-center"
                />
              </div>
              <span className="text-muted-foreground font-bold">–</span>
              <div className="flex-1">
                <Input type="number"
                  min={0}
                  max={7}
                  placeholder={awayLabel}
                  value={set.away}
                  onChange={(e) => updateSet(i, "away", e.target.value === "" ? "" : Number(e.target.value))}
                  disabled={isWalkover}
                  className="text-center"
                />
              </div>
            </div>
            {set.tiebreak && (
              <div className="flex items-center gap-2 pl-4">
                <span className="text-xs text-muted-foreground">TB:</span>
                <Input type="number"
                  min={0}
                  className="w-16 text-center h-8"
                  value={set.tiebreak_home}
                  onChange={(e) => updateSet(i, "tiebreak_home", e.target.value === "" ? "" : Number(e.target.value))}
                />
                <span className="text-muted-foreground">–</span>
                <Input type="number"
                  min={0}
                  className="w-16 text-center h-8"
                  value={set.tiebreak_away}
                  onChange={(e) => updateSet(i, "tiebreak_away", e.target.value === "" ? "" : Number(e.target.value))}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Comment */}
      <div>
        <Label htmlFor="comment" className="text-xs">Kommentar (valfri)</Label>
        <Textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
        />
      </div>

      <Separator />

      {/* Points preview */}
      {validSets.length > 0 && (
        <div className="text-center text-sm">
          <p className="text-muted-foreground">Poängfördelning</p>
          <p className="text-lg font-bold">
            {points.home} – {points.away}
          </p>
        </div>
      )}

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onClose}>
          Avbryt
        </Button>
        <Button
          className="flex-1"
          disabled={!isValid || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Sparar..." : "Skicka resultat"}
        </Button>
      </div>
    </div>
  );
}
